import { AxiosInstance, AxiosResponse } from 'axios'

import { ClientCookieManager, cookiesDataByName } from '@marketplace-web/shared/cookies'
import { logWarning } from '@marketplace-web/shared/logging'

import { HEADERS } from 'constants/header'
import { ResponseCode } from 'data/api/response-codes'

import { PROXIED_API_BASE_PATH } from '../constants/paths'

const cookies = new ClientCookieManager()

export const csrfTokenInterceptor = (instance: AxiosInstance) =>
  instance.interceptors.request.use(config => {
    const csrfToken = process.env.NEXT_PUBLIC_CSRF_TOKEN

    if (!csrfToken) throw new Error('Missing Next.js CSRF token')

    config.headers.set(HEADERS.X_CSRF_TOKEN, csrfToken)

    return config
  })

export const errorInterceptor = (instance: AxiosInstance) =>
  instance.interceptors.response.use((response: AxiosResponse) => {
    if (typeof response.data.code === 'undefined') {
      logWarning(
        `API code is missing in response: ${response.request.responseURL} with status: ${response.status}`,
      )
    }

    if (response.data.code && response.data.code !== ResponseCode.Ok) {
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({ response })
    }

    return Promise.resolve(response)
  })

export const localeInterceptor = (instance: AxiosInstance) =>
  instance.interceptors.request.use(config => {
    const locale = document.querySelector('meta[name="accept-language"]')?.getAttribute('content')

    if (!locale) {
      logWarning('Unable to retrieve locale for API call')
    }

    if (locale) config.headers.set(HEADERS.ACCEPT_LANGUAGE, locale)

    return config
  })

export const anonIdInterceptor = (instance: AxiosInstance) => {
  instance.interceptors.request.use(config => {
    const anonId = cookies.get(cookiesDataByName.anon_id)

    config.headers.set(HEADERS.X_ANON_ID, anonId)

    return config
  })
}

export const appendAcceptWebPHeaderInterceptor = (instance: AxiosInstance) =>
  instance.interceptors.request.use(config => {
    if (!config.headers.hasAccept('image/webp')) {
      const accept = config.headers.getAccept()?.toString().split(',') ?? []

      accept.push('image/webp')

      config.headers.setAccept(accept)
    }

    return config
  })

export const proxyApiV2ToMarketplaceWeb = (instance: AxiosInstance) =>
  instance.interceptors.request.use(config => ({
    ...config,
    baseURL: PROXIED_API_BASE_PATH,
  }))
